import { useState, useEffect } from "react";
import { HiMenu, HiX } from "react-icons/hi";
import { FaChevronDown } from "react-icons/fa";
import { createPortal } from "react-dom";
import menu from "../../../common/images/toggle.png";
import logo from "../../../common/images/logo-2.webp";

import '../../../styles/scrollbar.css';
import ManageBookingModal from "../Auth/ManageBookingModal";
import { useNavigate } from "react-router-dom";
const BottomHeader = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activePath, setActivePath] = useState(window.location.pathname);
  const [showAirportDropdown, setShowAirportDropdown] = useState(false);
  const [showMobileAirportDropdown, setShowMobileAirportDropdown] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('authToken'));

  useEffect(() => {
    const handleRouteChange = () => setActivePath(window.location.pathname);
    const handleAuthChanged = (e) => {
      if (e && e.detail && typeof e.detail.isLoggedIn === 'boolean') {
        setIsLoggedIn(e.detail.isLoggedIn);
      } else {
        // fallback: re-check localStorage
        setIsLoggedIn(!!localStorage.getItem('authToken'));
      }
    };
    window.addEventListener("popstate", handleRouteChange);
    window.addEventListener('authChanged', handleAuthChanged);
    return () => {
      window.removeEventListener("popstate", handleRouteChange);
      window.removeEventListener('authChanged', handleAuthChanged);
    };
  }, []);

  // Handle menu animation
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const navItems = ["Home", "Airport Parking", "Faqs", "Contact Us"];

  const airports = [
    { name: "Heathrow", url: "/heathrow-airport-parking" },
    { name: "Gatwick", url: "/gatwick-airport-parking" },
    { name: "Manchester", url: "/manchester-airport-parking" },
    { name: "Stansted", url: "/stansted-airport-parking" },
    { name: "Bristol", url: "/bristol-airport-parking" },
    { name: "Luton", url: "/luton-airport-parking" },
    { name: "Birmingham", url: "/birmingham-airport-parking" },
  ];

  return (
    <>
      <nav className="font-poppins relative z-50 max-w-[1400px] mx-auto bg-[#EEFE3E] backdrop-blur-md md:rounded-full rounded-md border border-white/10 shadow-2xl md:bg-transparent md:backdrop-blur-none md:border-none md:shadow-none">
        <div className="relative group w-full">
          {/* Asymmetrical Shadow Layer */}
          <div className="absolute inset-0 bg-black/10 blur-xl translate-y-2 md:rounded-full rounded-md -z-10 group-hover:bg-black/20 transition-all duration-500 hidden md:block"></div>

          {/* SVG Background Layer */}
          <div className="absolute inset-0 -z-10 hidden md:block">
            <svg
              viewBox="0 0 1400 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full drop-shadow-xl overflow-visible"
              preserveAspectRatio="none"
            >
              {/* Left Logo Section (Lime #EEFE3E) */}
              <path
                d="M 60,20 
                   C 10,20 10,100 60,100 
                   H 280 
                   C 360,100 360,20 280,20 
                   H 60 Z"
                fill="#EEFE3E"
              />

              {/* Right Navigation Section (Dark Green #00261E) */}
              {/* Tapered shape: starts narrow at x=310, grows taller towards x=1400 */}
              <path
                d="M 310,30 
                   C 360,30 360,90 310,90 
                   H 1340 
                   C 1390,90 1390,10 1340,10 
                   L 310,30 Z"
                fill="#00261E"
                className="hidden" /* This was a test path, using better one below */
              />

              {/* Final refined liquid merge path */}
              <path
                d="M 1340,0
                   C 1373,0 1400,27 1400,60
                   C 1400,93 1373,120 1340,120
                   L 360,105
                   C 300,105 300,15 360,15
                   L 1340,0 Z"
                fill="#00261E"
              />

              {/* Lime section overlapping with smooth curve */}
              <path
                d="M 60,15
                   C 27,15 0,42 0,75
                   C 0,108 27,135 60,135
                   H 300
                   C 380,135 380,15 300,15
                   H 60 Z"
                fill="#EEFE3E"
                transform="translate(0, -15)"
              />
            </svg>
          </div>

          {/* Content Layer */}
          <div className="flex items-center h-16 md:h-32 px-0 md:px-10 relative z-20">
            {/* Logo Area */}
            <div className="flex-shrink-0 w-auto md:w-[22%] flex justify-center items-center h-full pl-2 md:pl-0 md:translate-x-3">
              <a key="home" href="/" onClick={() => setActivePath("/")} className="flex items-center">
                <img
                  src={logo}
                  alt="Park Plus Airport Parking Logo"
                  className="h-10 md:h-20 w-auto brightness-90 contrast-125"
                />
              </a>
            </div>

            {/* Navigation Area (Dark Green #00261E) */}
            <div className="flex-1 flex items-center justify-end md:justify-between pl-2 md:pl-20 pr-4 md:pr-8 h-full">
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-6 lg:gap-10">
                {navItems.map((item, index) => {
                  if (item === "Airport Parking") {
                    return (
                      <div key={index} className="relative group/nav">
                        <button
                          className="flex items-center text-white font-black hover:text-[#EEFE3E] transition-all duration-300"
                          onMouseEnter={() => setShowAirportDropdown(true)}
                          onMouseLeave={() => setShowAirportDropdown(false)}
                        >
                          <span className="mr-2 uppercase text-[12px] tracking-widest leading-none">Airport Parking</span>
                          <FaChevronDown className={`text-[10px] transition-transform duration-300 ${showAirportDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Desktop Dropdown */}
                        {showAirportDropdown && (
                          <div
                            className="absolute left-0 z-50 mt-0 pt-6 w-72"
                            onMouseEnter={() => setShowAirportDropdown(true)}
                            onMouseLeave={() => setShowAirportDropdown(false)}
                          >
                            <div className="bg-white border-2 rounded-3xl shadow-2xl border-[#EEFE3E]/20 overflow-hidden transform animate-fade-in">
                              <div className="p-3 max-h-[400px] overflow-y-auto custom-scrollbar">
                                {airports.map((airport, airportIndex) => (
                                  <a
                                    key={airportIndex}
                                    href={airport.url}
                                    className="flex items-center justify-between px-5 py-2 text-gray-700 hover:bg-[#00261E] hover:text-white transition-all duration-300 rounded-2xl mb-1.5 last:mb-0 group/item"
                                    onClick={() => setShowAirportDropdown(false)}
                                  >
                                    <span className="text-sm font-black uppercase tracking-tight">{airport.name}</span>
                                    <span className="text-xs opacity-0 group-hover/item:opacity-100 translate-x-2 group-hover/item:translate-x-0 transition-all">→</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  const path = item === "Home" ? "/" : `/${item.toLowerCase().replace(/\s+/g, "-")}`;
                  const isActive = activePath === path;

                  return (
                    <a
                      key={index}
                      href={path}
                      className={`flex items-center text-white font-black hover:text-[#EEFE3E] transition-all duration-300 uppercase text-[12px] tracking-widest relative px-2 py-1`}
                      onClick={() => setActivePath(path)}
                    >
                      {isActive && (
                        <span className="absolute -bottom-1 left-2 right-2 h-[2px] bg-[#EEFE3E] rounded-full"></span>
                      )}
                      {item}
                    </a>
                  );
                })}
              </div>

              {/* Right side actions */}
              <div className="flex items-center gap-4 md:gap-6">
                <div className="hidden md:block">
                  {isLoggedIn ? (
                    <a href="/manage-booking" className="px-10 py-3.5 bg-[#EEFE3E] text-[#00261E] font-black text-xs md:text-[13px] rounded-full hover:bg-white hover:scale-110 active:scale-95 transition-all duration-300 shadow-xl uppercase tracking-widest">
                      DASHBOARD
                    </a>
                  ) : (
                    <button
                      onClick={() => setShowManageModal(true)}
                      className="px-10 py-3.5 bg-[#EEFE3E] text-[#00261E] font-black text-xs md:text-[13px] rounded-full hover:bg-white hover:scale-110 active:scale-95 transition-all duration-300 shadow-xl uppercase tracking-widest"
                    >
                      LOGIN
                    </button>
                  )}
                </div>

                {/* Mobile Menu Toggle */}
                <button
                  className="text-black md:hidden p-3 rounded-full hover:bg-white/10 transition-colors"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {isOpen ? (
                    <HiX className="text-2xl" />
                  ) : (
                    <HiMenu className="text-2xl" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Slide-in Menu Portal */}
      {(isOpen || isAnimating) &&
        createPortal(
          <div className="fixed inset-0 md:hidden" style={{ zIndex: 999999 }}>
            {/* Backdrop */}
            <div
              className={`absolute inset-0 bg-black transition-all duration-300 ease-in-out ${isOpen ? "bg-opacity-50" : "bg-opacity-0"
                }`}
              onClick={() => setIsOpen(false)}
            />

            {/* Slide-in Menu from Right */}
            <div
              className={`absolute top-0 right-0 h-full w-[100%] bg-brandDark shadow-2xl transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
              {/* Menu Header with Close Button */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h2 className="text-lg font-bold tracking-widest text-white uppercase">
                  Menu
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center w-8 h-8 text-white transition-colors duration-200 rounded-full hover:bg-white/10"
                >
                  <HiX className="text-xl" />
                </button>
              </div>

              {/* Menu Items */}
              <div className="py-2">
                {navItems.map((item, index) => {
                  if (item === "Airport Parking") {
                    return (
                      <div key={index}>
                        <button
                          className={`flex items-center justify-between w-full px-4 py-4 text-sm font-bold text-white uppercase tracking-widest transition-colors duration-200 border-b border-white/5`}
                          onClick={() => setShowMobileAirportDropdown(!showMobileAirportDropdown)}
                        >
                          <span>AIRPORT PARKING</span>
                          <FaChevronDown className={`text-[12px] transition-transform duration-200 ${showMobileAirportDropdown ? 'rotate-180' : ''
                            }`} />
                        </button>

                        {/* Mobile Airport Dropdown */}
                        {showMobileAirportDropdown && (
                          <div className="bg-black/20 max-h-[300px] overflow-y-auto custom-scrollbar backdrop-blur-sm">
                            {airports.map((airport, airportIndex) => (
                              <a
                                key={airportIndex}
                                href={airport.url}
                                className="flex items-center justify-between px-6 py-4 text-sm text-white transition-all duration-200 border-b border-white/5 group hover:bg-brandLime hover:text-brandDark"
                                onClick={() => {
                                  setIsOpen(false);
                                  setShowMobileAirportDropdown(false);
                                }}
                                role="menuitem"
                                tabIndex={0}
                              >
                                <span className="font-bold">{airport.name} Airport Parking</span>
                                <span className="text-xs transition-all transform opacity-0 group-hover:opacity-100 group-hover:translate-x-1">→</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  const path =
                    item === "Home"
                      ? "/"
                      : `/${item.toLowerCase().replace(/\s+/g, "-")}`;
                  return (
                    <a
                      key={index}
                      href={path}
                      className={`flex items-center px-4 py-4 text-sm font-bold text-white uppercase tracking-widest transition-colors duration-200 border-b border-white/5 hover:bg-white/5 ${activePath === path ? "bg-white/10 text-brandLime" : ""
                        }`}
                      onClick={() => {
                        setIsOpen(false);
                        setActivePath(path);
                      }}
                    >
                      <span>{item.toUpperCase()}</span>
                    </a>
                  );
                })}
              </div>
              <div className="p-6">
                {isLoggedIn ? (
                  <a href="/manage-booking" className="block w-full py-4 font-black text-center bg-brandLime rounded-full text-brandDark uppercase tracking-widest text-sm shadow-lg">
                    Dashboard
                  </a>
                ) : (
                  <button
                    onClick={() => setShowManageModal(true)}
                    className="w-full py-4 font-black bg-brandLime rounded-full text-brandDark uppercase tracking-widest text-sm shadow-lg"
                  >
                    Manage Booking
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      <ManageBookingModal open={showManageModal} onClose={() => setShowManageModal(false)} onSuccess={() => { setShowManageModal(false); setIsLoggedIn(true); window.dispatchEvent(new CustomEvent('authChanged', { detail: { isLoggedIn: true } })); navigate('/manage-booking'); }} />
    </>
  );
};

export default BottomHeader;
